// A sequential, cached queue for rendering area thumbnails with one shared offscreen map. Pure: no React and no
// maplibre import, so it runs under jest with a fake renderer. The real renderer is in maplibreThumbnailRenderer.
import type { Coord } from '../utils/areaThumbnail';

export interface ThumbnailJob {
  key: string;
  view: { center: Coord; zoom: number };
  dark: boolean;
  width: number;
  height: number;
}

export interface ThumbnailRenderer {
  render(job: ThumbnailJob): Promise<string>;
  dispose(): void;
}

export type RendererFactory = () => Promise<ThumbnailRenderer>;

export interface QueueOptions {
  createRenderer: RendererFactory;
  maxEntries?: number;
  idleDisposeMs?: number;
}

export interface ThumbnailQueue {
  /** Cached data URL for a key, or undefined. */
  peek(key: string): string | undefined;
  /** Resolves with the data URL; rejects on failure. `release` drops this subscriber (the job is dropped if nobody waits and it has not started). */
  request(job: ThumbnailJob): { promise: Promise<string>; release(): void };
  /** Test hook: clear the cache and pending jobs and dispose the renderer. */
  reset(): void;
}

export const MAX_CACHE_ENTRIES = 150;
export const IDLE_DISPOSE_MS = 30000;

export function thumbnailKey(areaIds: string[], dark: boolean, w: number, h: number): string {
  return `${areaIds.join('|')}::${dark ? 'dark' : 'light'}::${w}x${h}`;
}

interface Subscriber {
  resolve(url: string): void;
  reject(err: unknown): void;
}

interface Entry {
  job: ThumbnailJob;
  subs: Set<Subscriber>;
  running: boolean;
}

export function createThumbnailQueue({
  createRenderer,
  maxEntries = MAX_CACHE_ENTRIES,
  idleDisposeMs = IDLE_DISPOSE_MS,
}: QueueOptions): ThumbnailQueue {
  const cache = new Map<string, string>();
  const entries = new Map<string, Entry>(); // pending and running, oldest first
  let renderer: ThumbnailRenderer | null = null;
  let loadedDark: boolean | undefined;
  let busy = false;
  let scheduled = false;
  let idleTimer: ReturnType<typeof setTimeout> | undefined;

  const remember = (key: string, url: string) => {
    cache.delete(key);
    cache.set(key, url);
    while (cache.size > maxEntries) cache.delete(cache.keys().next().value as string);
  };

  const disposeRenderer = () => {
    if (idleTimer !== undefined) clearTimeout(idleTimer);
    idleTimer = undefined;
    renderer?.dispose();
    renderer = null;
    loadedDark = undefined;
  };

  const pickNext = (): Entry | undefined => {
    const pending = [...entries.values()].filter((e) => !e.running);
    if (loadedDark !== undefined) {
      const same = pending.find((e) => e.job.dark === loadedDark);
      if (same) return same;
    }
    return pending[0];
  };

  const pump = async (): Promise<void> => {
    if (busy) return;
    const next = pickNext();
    if (!next) {
      if (renderer && idleTimer === undefined) idleTimer = setTimeout(disposeRenderer, idleDisposeMs);
      return;
    }
    busy = true;
    if (idleTimer !== undefined) clearTimeout(idleTimer);
    idleTimer = undefined;
    next.running = true;
    let url: string | undefined;
    let failure: unknown;
    let failed = false;
    try {
      if (!renderer) renderer = await createRenderer();
      loadedDark = next.job.dark;
      url = await renderer.render(next.job);
    } catch (err) {
      failed = true;
      failure = err;
    }
    entries.delete(next.job.key);
    busy = false;
    if (failed) next.subs.forEach((s) => s.reject(failure));
    else {
      remember(next.job.key, url as string);
      next.subs.forEach((s) => s.resolve(url as string));
    }
    void pump();
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    // A microtask, so jobs requested in the same tick are all visible when the next one is picked.
    void Promise.resolve().then(() => {
      scheduled = false;
      return pump();
    });
  };

  return {
    peek(key) {
      const url = cache.get(key);
      if (url !== undefined) remember(key, url);
      return url;
    },
    request(job) {
      const hit = cache.get(job.key);
      if (hit !== undefined) {
        remember(job.key, hit);
        return { promise: Promise.resolve(hit), release() {} };
      }
      let sub!: Subscriber;
      const promise = new Promise<string>((resolve, reject) => {
        sub = { resolve, reject };
      });
      let entry = entries.get(job.key);
      if (!entry) {
        entry = { job, subs: new Set(), running: false };
        entries.set(job.key, entry);
      }
      entry.subs.add(sub);
      const mine = entry;
      schedule();
      return {
        promise,
        release() {
          mine.subs.delete(sub);
          if (!mine.running && mine.subs.size === 0 && entries.get(job.key) === mine) entries.delete(job.key);
        },
      };
    },
    reset() {
      cache.clear();
      entries.clear();
      disposeRenderer();
    },
  };
}
